import { TrendingUp } from "lucide-react"
import { Bar, BarChart, XAxis, YAxis } from "recharts"

import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import { useQuery } from "@tanstack/react-query"
import api from "@/lib/axios"

interface DataJumlahPendaftaran {
  nama_prodi: string
  jumlah_pendaftaran: number
  warna: string
}

interface ChartConfig {
  [key: string]: {
    label: string
    color: string
  }
}

const getDataJumlahPendaftaran = async (tahun?: string | number): Promise<DataJumlahPendaftaran[]> => {
  const response = await api.get(
    "/operator-internasional/dashboard/jumlah-pendaftaran",
    {
      params: {
        tahun,
      },
    }
  )
  return response.data?.data
}

export function BarChartComponent({
  tahun,
}: {
  tahun: string | number | undefined
}) {
  const { data: dataJumlahPendaftaran = [] } = useQuery<
    DataJumlahPendaftaran[]
  >({
    queryKey: ["dataJumlahPendaftaran", tahun],
    queryFn: () => getDataJumlahPendaftaran(tahun),
  })

  // Konversi data API menjadi format untuk chartConfig
  const chartConfig = dataJumlahPendaftaran.reduce<ChartConfig>(
    (config, item) => {
      config[item.nama_prodi] = {
        label: item.nama_prodi,
        color: item.warna,
      }
      return config
    },
    {}
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pendaftaran Siswa / Program Studi</CardTitle>
        <CardDescription>Tahun {tahun}</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto">
        <ChartContainer config={chartConfig} className="h-[300vh] w-full">
          <BarChart
            accessibilityLayer
            data={dataJumlahPendaftaran.map((item) => ({
              nama_prodi: item.nama_prodi,
              jumlah_pendaftaran: item.jumlah_pendaftaran,
              fill: item.warna,
            }))}
            layout="vertical"
            margin={{
              left: 20,
            }}
          >
            <YAxis
              dataKey="nama_prodi"
              type="category"
              width={300}
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              tickFormatter={(value) =>
                chartConfig[value as keyof typeof chartConfig]?.label ?? value
              }
              className="text-xs"
              interval={0}
              minTickGap={20}
            />
            <XAxis
              dataKey="jumlah_pendaftaran"
              type="number"
              hide
              domain={[0, 10]}
            />
            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
            />
            <Bar dataKey="jumlah_pendaftaran" layout="vertical" radius={5} />
          </BarChart>
        </ChartContainer>
      </CardContent>
      <CardFooter className="flex-col items-start gap-2 text-sm">
        <div className="flex gap-2 font-medium leading-none">
          Chart jumlah data pendaftaran siswa / program studi{" "}
          <TrendingUp className="h-4 w-4" />
        </div>
        <div className="leading-none text-muted-foreground">
          Menampilkan jumlah data pendaftaran siswa per program studi selama
          tahun {tahun}
        </div>
      </CardFooter>
    </Card>
  )
}
